/**
 * Arrivals — flat list of ARRIVAL events from v2_tag_event. One row
 * per tag the upstream pipeline classified as reliably arrived
 * (>=2 confident reads). Mirror of DeparturesPage with two
 * differences:
 *   - No drill-down modal — each event IS the unit (there's no
 *     parent ticket / camera-episode aggregation).
 *   - Gate column shows NEW_ARRIVAL vs RE_ARRIVAL; the latter is
 *     the trigger that also produces a CANCEL row on the Departures
 *     ledger.
 *
 * Visibility: all authenticated roles; server scopes by SCU
 * access. Vendor sessions additionally see only EPCs whose item
 * belongs to their catalog.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtAgo, fmtUtcInTz, fmtWorkCenter, shortScuName, tzFromLatLon } from "../lib/format";
import {
  type ArrivalGate,
  type ArrivalRow,
  listArrivals,
} from "../services/arrivalsApi";

const GATE_PILL: Record<ArrivalGate, string> = {
  NEW_ARRIVAL: "bg-success-100 text-success-700",
  RE_ARRIVAL:  "bg-warning-100 text-warning-700",
};

const RELIABILITY_PILL: Record<string, string> = {
  HEALTHY:     "bg-success-100 text-success-700",
  MOSTLY_READ: "bg-success-50  text-success-700",
  FLICKERY:    "bg-warning-100 text-warning-700",
  UNRELIABLE:  "bg-error-100   text-error-700",
};

function mtdDefaults(): { since: string; until: string } {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return { since: `${yyyy}-${mm}-01`, until: `${yyyy}-${mm}-${dd}` };
}

export const ArrivalsPage: React.FC = () => {
  const { token, role } = useAuth();
  // Confidence (reliability bucket) is an operator triage signal — hide
  // from customers and vendors (Jason 2026-06-16).
  const showConfidence = role === "platform";
  const { snapshot: boxSnapshot } = useScopedBoxes();
  const [rows, setRows] = useState<ArrivalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialDates = useMemo(() => mtdDefaults(), []);
  const [searchFilter, setSearchFilter] = useState("");
  const [sinceFilter, setSinceFilter] = useState<string>(initialDates.since);
  const [untilFilter, setUntilFilter] = useState<string>(initialDates.until);

  // Per-column refinements (Jason 2026-06-11). SmartBox + Timestamp
  // are excluded — the top-bar search handles smartbox; the date
  // range handles timestamp.
  const [eventFilter, setEventFilter] = useState("");
  const [gateFilter, setGateFilter] = useState<"" | ArrivalGate>("");
  const [itemFilter, setItemFilter] = useState("");
  const [epcFilter, setEpcFilter] = useState("");
  const [reliabilityFilter, setReliabilityFilter] = useState<string>("");

  // Server-side EPC search (Jason 2026-06-16). When set, ignores the
  // date range and fetches the EPC's full arrival history.
  const [epcSearch, setEpcSearch] = useState("");

  const [offset, setOffset] = useState(0);
  const LIMIT = 200;

  const scuCoords = useMemo(() => {
    const m = new Map<string, readonly [number | null, number | null]>();
    for (const b of boxSnapshot?.boxes ?? []) {
      m.set(b.scu_name, [b.lat, b.lon] as const);
    }
    return m;
  }, [boxSnapshot]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const trimmedEpc = epcSearch.trim();
      const p = await listArrivals(token, {
        since: trimmedEpc ? undefined : sinceFilter ? `${sinceFilter}T00:00:00Z` : undefined,
        until: trimmedEpc ? undefined : untilFilter ? `${untilFilter}T23:59:59Z` : undefined,
        epc: trimmedEpc || undefined,
        limit: LIMIT,
        offset,
      });
      setRows(p.data);
      setTotal(p.pagination.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, sinceFilter, untilFilter, offset, epcSearch]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => { setOffset(0); }, [sinceFilter, untilFilter]);

  const visibleRows = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    const ev = eventFilter.trim().toLowerCase();
    const it = itemFilter.trim().toLowerCase();
    const ep = epcFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(
        (r.smartbox_code ?? "").toLowerCase().includes(q) ||
        (r.work_center ?? "").toLowerCase().includes(q) ||
        r.scu_name.toLowerCase().includes(q)
      )) return false;
      if (ev && !String(r.tag_event_id).toLowerCase().includes(ev)) return false;
      if (gateFilter && r.gate !== gateFilter) return false;
      if (it && !(
        (r.sku ?? "").toLowerCase().includes(it) ||
        (r.item_name ?? "").toLowerCase().includes(it) ||
        (r.vendor_name ?? "").toLowerCase().includes(it)
      )) return false;
      if (ep && !r.epc.toLowerCase().includes(ep)) return false;
      if (reliabilityFilter && r.reliability_bucket !== reliabilityFilter) return false;
      return true;
    });
  }, [rows, searchFilter, eventFilter, gateFilter, itemFilter, epcFilter, reliabilityFilter]);

  const hasNext = offset + LIMIT < total;
  const hasPrev = offset > 0;

  const headerCount = useMemo(() => {
    if (loading && rows.length === 0) return "loading…";
    if (total === 0) return "0";
    if (searchFilter.trim()) {
      return `${visibleRows.length.toLocaleString()} match · ${total.toLocaleString()} loaded`;
    }
    const from = offset + 1;
    const to = Math.min(offset + rows.length, total);
    return `${from}–${to} of ${total.toLocaleString()}`;
  }, [loading, rows.length, total, offset, searchFilter, visibleRows.length]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold">Arrivals</h1>
        </div>
        <div className="text-xs text-secondary-500">{headerCount}</div>
      </div>

      <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3 mb-3 space-y-2">
        <label className="block text-xs text-secondary-600">
          <div className="flex items-center justify-between">
            <span>EPC search (full history, ignores date range)</span>
            {epcSearch && (
              <button
                type="button"
                onClick={() => setEpcSearch("")}
                className="text-[10px] text-primary-600 hover:underline"
              >
                clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={epcSearch}
            onChange={(e) => setEpcSearch(e.target.value)}
            placeholder="paste or scan an EPC to find every arrival for that tag"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <label className="text-xs text-secondary-600">
            <div>Smartbox / Work center</div>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="SB071, X24, …"
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-secondary-600">
            <div>Since {epcSearch && <span className="text-secondary-400">(EPC search ignores)</span>}</div>
            <input
              type="date"
              value={sinceFilter}
              onChange={(e) => setSinceFilter(e.target.value)}
              disabled={!!epcSearch}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm disabled:bg-secondary-100"
            />
          </label>
          <label className="text-xs text-secondary-600">
            <div>Until {epcSearch && <span className="text-secondary-400">(EPC search ignores)</span>}</div>
            <input
              type="date"
              value={untilFilter}
              onChange={(e) => setUntilFilter(e.target.value)}
              disabled={!!epcSearch}
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm disabled:bg-secondary-100"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded mb-3">
          {error}
        </div>
      )}

      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">Gate</th>
              <th className="text-left px-3 py-2">SmartBox</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">EPC</th>
              <th
                className="text-left px-3 py-2"
                title="Rendered in the smartbox's local time (Central by default)"
              >Timestamp</th>
              {showConfidence && <th className="text-left px-3 py-2">Confidence</th>}
            </tr>
            {/* Per-column refinement row. SmartBox + Timestamp omitted
                — the top-bar search and date range already cover them. */}
            <tr className="bg-white border-t border-secondary-200 normal-case">
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                  placeholder="filter"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs"
                />
              </th>
              <th className="px-2 pb-1.5 font-normal">
                <select
                  value={gateFilter}
                  onChange={(e) => setGateFilter(e.target.value as "" | ArrivalGate)}
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs bg-white normal-case"
                >
                  <option value="">All</option>
                  <option value="NEW_ARRIVAL">NEW</option>
                  <option value="RE_ARRIVAL">RE-ARRIVAL</option>
                </select>
              </th>
              <th className="px-2 pb-1.5"></th>
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={itemFilter}
                  onChange={(e) => setItemFilter(e.target.value)}
                  placeholder="sku / name / vendor"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs"
                />
              </th>
              <th className="px-2 pb-1.5 font-normal">
                <input
                  type="text"
                  value={epcFilter}
                  onChange={(e) => setEpcFilter(e.target.value)}
                  placeholder="filter"
                  className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono"
                />
              </th>
              <th className="px-2 pb-1.5"></th>
              {showConfidence && (
                <th className="px-2 pb-1.5 font-normal">
                  <select
                    value={reliabilityFilter}
                    onChange={(e) => setReliabilityFilter(e.target.value)}
                    className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs bg-white normal-case"
                  >
                    <option value="">All</option>
                    <option value="HEALTHY">HEALTHY</option>
                    <option value="MOSTLY_READ">MOSTLY_READ</option>
                    <option value="FLICKERY">FLICKERY</option>
                    <option value="UNRELIABLE">UNRELIABLE</option>
                  </select>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-secondary-500">
                  loading…
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-secondary-500">
                  No arrivals match the current filters.
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => {
                const c = r.is_cancelled;
                const rowTint = c ? "bg-error-50/30 hover:bg-error-50" : "hover:bg-secondary-50";
                const strike = c ? "line-through text-secondary-500" : "";
                return (
                <tr key={r.tag_event_id} className={`${rowTint} align-top`}>
                  <td
                    className="px-3 py-2 font-mono text-secondary-700"
                    title={
                      c
                        ? `Cancelled — same-week reversal of Departure #${r.paired_event_id ?? "?"}`
                        : "v2_tag_event.tag_event_id"
                    }
                  >
                    {c && (
                      <span className="text-error-700 mr-1 text-[10px]" title="Reversed">⊘</span>
                    )}
                    #{r.tag_event_id}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] " +
                        GATE_PILL[r.gate]
                      }
                      title={
                        r.gate === "RE_ARRIVAL"
                          ? c
                            ? "Re-arrival inside the customer's cancellation window — paired with the prior Departure and reversed."
                            : "Tag re-arrived after a prior DEPARTURE."
                          : "Tag never seen before at this SmartBox."
                      }
                    >
                      {r.gate === "NEW_ARRIVAL" ? "NEW" : "RE-ARRIVAL"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {r.smartbox_code ?? "—"}
                    {r.work_center && (
                      <span className="ml-1.5 text-secondary-600 font-normal">
                        · {fmtWorkCenter(r.work_center)}
                      </span>
                    )}
                    <div
                      className="text-[10px] text-secondary-500 font-mono"
                      title={r.scu_name}
                    >
                      {shortScuName(r.scu_name)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className={"font-mono " + strike}>
                      {r.sku ?? (r.item_id ? `#${r.item_id}` : "—")}
                    </div>
                    {r.item_name && (
                      <div className={"text-[10px] " + (c ? "line-through text-secondary-400" : "text-secondary-500")}>
                        {r.item_name}
                      </div>
                    )}
                    {r.vendor_name && (
                      <div className="text-[10px] text-secondary-500">{r.vendor_name}</div>
                    )}
                  </td>
                  <td className={"px-3 py-2 font-mono " + strike}>{r.epc}</td>
                  <td className="px-3 py-2 text-secondary-700">
                    {(() => {
                      const coord = scuCoords.get(r.scu_name);
                      const tz = tzFromLatLon(coord?.[0], coord?.[1]);
                      return (
                        <>
                          {fmtUtcInTz(r.event_time, tz)}
                          {r.event_time && (
                            <div className="text-[10px] text-secondary-500">
                              {fmtAgo(r.event_time)} ago
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  {showConfidence && (
                    <td className="px-3 py-2">
                      {r.reliability_bucket ? (
                        <span
                          className={
                            "inline-flex items-center rounded px-1.5 py-0.5 " +
                            (RELIABILITY_PILL[r.reliability_bucket] ??
                              "bg-secondary-100 text-secondary-700")
                          }
                        >
                          {r.reliability_bucket}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-secondary-600">
        <div>
          {hasPrev && (
            <button
              className="underline hover:text-secondary-900"
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              ← Newer
            </button>
          )}
        </div>
        <div>
          {hasNext && (
            <button
              className="underline hover:text-secondary-900"
              onClick={() => setOffset(offset + LIMIT)}
            >
              Older →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArrivalsPage;
